<script setup lang="ts">
import { $date, useHttpClient } from "@/vf"
import { computed, onMounted, ref, watch } from "vue"
import type { AxiosError } from "axios"
import { useShopDialogs } from "@/shop/composables/useShopDialogs.ts"

interface RevocableOrderItem {
    id: string
    articleName: string
    displayAmount: string
    displayUnit: string
    displayTotalAmount: string
}

interface RevocableOrder {
    id: string
    orderNumber: string
    createdAt: string
    items: RevocableOrderItem[]
}

const http = useHttpClient()
const dialogs = useShopDialogs()

const error = ref<string | undefined>(undefined)
const isSubmitting = ref(false)

const step = ref(0)

const comment = ref("")
const orderNumber = ref("")
const orders = ref<RevocableOrder[]>([])
const selectedOrderItemIds = ref<string[]>([])
const selectedOrder = computed<RevocableOrder | undefined>(
    () => orders.value.find(o => o.orderNumber === orderNumber.value) ?? undefined,
)

onMounted(async () => {
    try {
        const { data } = await http.get<RevocableOrder[]>("/my-account/revokable-orders")
        orders.value = data
    } catch {
        orders.value = []
        await dialogs.alert("Fehler", "Widerrufbare Bestellungen konnten nicht geladen werden.")
    }
})

watch(
    () => selectedOrder.value?.id,
    () => {
        selectedOrderItemIds.value = []
    },
)

async function revokeOrder() {
    if (!selectedOrder.value) {
        await dialogs.alert("Fehler", "Es wurde keine Bestellung ausgewählt.")
        return
    }

    if (isSubmitting.value) {
        return
    }

    isSubmitting.value = true

    const loadingDialog = dialogs.loading("Widerruf wird gesendet")
    try {
        await http.post(`/my-account/revoke-order`, {
            orderNumber: selectedOrder.value!.orderNumber,
            comment: comment.value,
            selectedOrderItemIds: selectedOrderItemIds.value,
            orderId: selectedOrder.value!.id,
        })
    } catch (e) {
        loadingDialog.ok()
        isSubmitting.value = false

        error.value = (e as AxiosError<{ error: string }>).response?.data?.error
        if (error.value === "invalid email") {
            return
        }

        await dialogs.alert("Fehler", "Der Widerruf konnte nicht gesendet werden. Bitte versuche es erneut.")
        return
    }

    loadingDialog.ok()
    isSubmitting.value = false
    step.value = 3

    orderNumber.value = ""
    comment.value = ""
    selectedOrderItemIds.value = []
    error.value = undefined
}

function selectOrder(order: RevocableOrder) {
    orderNumber.value = order.orderNumber
    step.value = 1
    selectedOrderItemIds.value = []
}
</script>

<template>
    <div v-if="orders.length < 1">
        Es wurden keine widerrufbaren Bestellungen gefunden. Bestellungen, die von uns noch nicht bearbeitet wurden,
        kannst du in
        <RouterLink :to="{ name: 'my-account', hash: '#bestellungen' }">deinem Profil</RouterLink> stornieren. Für alle
        anderen Bestellungen <RouterLink :to="{ name: 'contact' }">kontaktiere</RouterLink> uns bitte direkt.
    </div>

    <div v-if="orders.length > 0">
        <div v-if="step === 0">
            <p>
                Bitte wähle die Bestellung, die du widerrufen möchtest. Bestellungen, die von uns noch nicht bearbeitet
                wurden, kannst du in
                <RouterLink :to="{ name: 'my-account', hash: '#bestellungen' }">deinem Profil</RouterLink> stornieren.
                Für alle anderen Bestellungen <RouterLink :to="{ name: 'contact' }">kontaktiere</RouterLink> uns bitte
                direkt.
            </p>
            <h2></h2>
            <ul class="list-group mt-3">
                <li
                    v-for="order in orders"
                    :key="order.id"
                    class="list-group-item c-pointer list-group-item-action"
                    @click="selectOrder(order)"
                >
                    Bestellung {{ order.orderNumber ?? "-" }} vom {{ $date(order.createdAt) }}
                </li>
            </ul>
        </div>

        <div v-if="selectedOrder && step === 1">
            <h2>Wähle die Artikel aus, die widerrufen werden sollen</h2>
            <ul class="list-group mt-3">
                <li
                    v-for="orderItem in selectedOrder.items"
                    :key="orderItem.id"
                    :class="{ active: selectedOrderItemIds.includes(orderItem.id) }"
                    class="list-group-item c-pointer list-group-item-action"
                >
                    <label class="d-flex justify-content-start mb-0 c-pointer">
                        <input
                            v-show="false"
                            v-model="selectedOrderItemIds"
                            :value="orderItem.id"
                            type="checkbox"
                            class="d-inline"
                        />
                        <div class="ml-3">
                            <div>{{ orderItem.articleName }}</div>
                            <div class="small text-muted">
                                ({{ orderItem.displayTotalAmount }} {{ orderItem.displayUnit }})
                            </div>
                        </div>
                    </label>
                </li>
            </ul>
            <div v-if="orders.length > 0" class="d-flex justify-content-between mt-3">
                <div>
                    <button class="btn btn-secondary" @click="step--">Zurück</button>
                </div>
                <div>
                    <button class="btn btn-primary" :disabled="selectedOrderItemIds.length <= 0" @click="step++">
                        Weiter
                    </button>
                </div>
            </div>
        </div>

        <div v-if="step === 2">
            <h2>Gibt es noch etwas, das du uns dazu mitteilen möchtest?</h2>
            <textarea v-model="comment" class="form-control mt-3" rows="7"></textarea>

            <div class="d-flex justify-content-between mt-3">
                <div>
                    <button class="btn btn-secondary" @click="step--">Zurück</button>
                </div>
                <div>
                    <button class="btn btn-danger ml-auto" :disabled="isSubmitting" @click="revokeOrder">
                        Widerruf bestätigen
                    </button>
                </div>
            </div>
        </div>

        <div v-if="step === 3">
            <h2>Vielen Dank</h2>

            <p>
                Dein Widerruf wurde von uns erfasst und wird nun geprüft. Da unser System die Voraussetzungen eines
                Widerrufs nicht vorab bewertet, erfolgt die rechtliche Prüfung erst im Rahmen der Bearbeitung. Über das
                Ergebnis der Prüfung und die weiteren Schritte informieren wir dich gesondert per Mail.
            </p>
        </div>
    </div>
</template>
